import React, { useEffect, useRef, useState } from 'react';
import Flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

export interface DateTimePickerProps {
    name?: string;
    value: string; // ISO format
    placeholder?: string;
    onChange: (val: string) => void;
    extendedClassName?: string;
    containerClassName?: string;
    testId: string;
    error?: boolean;
    disabled?: boolean;
    id?: string;
    allowInput?: boolean;
    enableTime?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
    const { error = false, disabled = false, name = '', id = '', allowInput = false, enableTime = true } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const flatpickrRef = useRef<Flatpickr.Instance | null>(null);
    const isTypingRef = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [previousValidValue, setPreviousValidValue] = useState('');

    const flatpickrFormat = enableTime ? 'Y-m-d H:i' : 'Y-m-d';

    // Format the selected date to match flatpickr format for display
    const formatDateForDisplay = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        if (enableTime) {
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        }
        return `${year}-${month}-${day}`;
    };

    // Validate input format
    const isValidFormat = (value: string) => {
        if (!value) return true; // Empty is valid

        const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
        const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (enableTime) {
            return dateTimeRegex.test(value);
        }
        return dateOnlyRegex.test(value);
    };

    // Update value directly without conversion
    const updateValue = (value: string) => {
        setInputValue(value);
        setPreviousValidValue(value);
        props.onChange(value);
    };

    // Handler for when a date is selected or cleared
    const handleDateChange = ([selected]: Date[]) => {
        if (!selected) {
            setInputValue('');
            props.onChange('');
            return;
        }

        const formattedValue = formatDateForDisplay(selected);
        updateValue(formattedValue);
    };

    // Handle manual input blur
    const handleOnClose = () => {
        isTypingRef.current = false;

        if (!allowInput || !inputRef.current) {
            return;
        }

        const typedValue = inputRef.current.value;

        // Validate format and revert if incorrect
        if (isValidFormat(typedValue)) {
            updateValue(typedValue);
        } else {
            // Revert to previous valid value
            setInputValue(previousValidValue);
            if (flatpickrRef.current && previousValidValue) {
                try {
                    const date = new Date(previousValidValue);
                    if (!isNaN(date.getTime())) {
                        flatpickrRef.current.setDate(date, false);
                    }
                } catch {
                    // Ignore parsing errors
                }
            }
        }
    };

    // Initialize Flatpickr on mount
    useEffect(() => {
        if (!inputRef.current) return;

        flatpickrRef.current = Flatpickr(inputRef.current, {
            enableTime: enableTime,
            enableSeconds: false,
            dateFormat: flatpickrFormat,
            defaultDate: props.value ? new Date(props.value) : undefined,
            allowInput: allowInput,
            time_24hr: true,
            onChange: handleDateChange,
            onClose: handleOnClose,
        });

        return () => {
            flatpickrRef.current?.destroy();
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Sync props.value -> input display
    useEffect(() => {
        if (!flatpickrRef.current || isTypingRef.current) {
            return;
        }

        if (!props.value) {
            flatpickrRef.current.clear();
            setInputValue('');
            return;
        }

        try {
            const date = new Date(props.value);
            if (!isNaN(date.getTime())) {
                const displayValue = formatDateForDisplay(date);
                flatpickrRef.current.setDate(date, false);
                setInputValue(displayValue);
                setPreviousValidValue(displayValue);
            } else {
                setInputValue(props.value); // Fallback to raw value if parsing fails
                setPreviousValidValue(props.value);
            }
        } catch {
            setInputValue(props.value); // Fallback to raw value if parsing fails
            setPreviousValidValue(props.value);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [props.value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!allowInput) {
            return;
        }

        isTypingRef.current = true;
        const newValue = e.target.value;
        setInputValue(newValue);

        // Only call onChange if format is valid, but allow typing to continue
        if (isValidFormat(newValue) || newValue === '') {
            props.onChange(newValue);
        }
    };

    // Reset typing flag on blur
    const handleBlur = () => {
        isTypingRef.current = false;

        if (!allowInput) {
            return;
        }

        // Validate format and revert if incorrect
        const currentValue = inputValue;
        if (!isValidFormat(currentValue) && currentValue !== '') {
            // Revert to previous valid value
            setInputValue(previousValidValue);
            if (flatpickrRef.current && previousValidValue) {
                try {
                    const date = new Date(previousValidValue);
                    if (!isNaN(date.getTime())) {
                        flatpickrRef.current.setDate(date, false);
                    }
                } catch {
                    // Ignore parsing errors
                }
            }
        } else if (isValidFormat(currentValue)) {
            // Update previous valid value if current is valid
            setPreviousValidValue(currentValue);
        }
    };

    // Input class name with error handling
    const defaultInputClassName =
        'input input-sm text-xs bg-white px-[12px] py-[6px] border rounded text-gray-900 block text-base font-normal leading-6 bg-clip-padding focus-within:outline-none focus-within:border-[#86b7fe] focus-within:shadow-[inset_0_1px_1px_rgba(0,0,0,0.08),0_0_8px_rgba(102,175,233,0.6)]';

    // Error
    const errorClassName = error
        ? 'input-error focus:border-red-500 focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.08),0_0_8px_rgba(255,99,71,0.6)]'
        : '';

    const disabledClass = disabled
        ? 'disabled:!border-custom-gray-06 disabled:bg-custom-gray-07 disabled:text-custom-black-01 disabled:opacity-100'
        : '';

    const inputClassName = [defaultInputClassName, props.extendedClassName, errorClassName, disabledClass]
        .filter(Boolean)
        .join(' ');

    // Container class name
    const containerClassName = `${props.containerClassName || ''}`;

    return (
        <div data-test-id={props.testId} className={containerClassName}>
            <input
                ref={inputRef}
                placeholder={props.placeholder || ''}
                className={inputClassName}
                disabled={disabled}
                name={name}
                id={id}
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </div>
    );
};

export default DateTimePicker;
